import { Text, View, Pressable, FlatList } from 'react-native';
import { useCallback, useMemo, useState } from 'react';
import useNotificationBadges from '@/utils/hooks/useNotificationBadges';
import NotificationBadge from '@/components/General/NotificationBadge';
import { StyleSheet } from 'react-native-unistyles';
import { LegendList } from "@legendapp/list";
import Avatar from '@/components/People/Avatar';
import ContentLoader from '@/components/General/ContentLoader';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SearchBox from '@/components/General/inputs/SearchBox';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { Query } from '@/utils/react-query/Queries';
import usePlatform from '@/utils/hooks/usePlatform';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteConversation, toggleConversationPin } from '@/data/connect';
import { showAlert } from '@/utils/showAlert';
import { useRouter } from 'expo-router';
import RelativeTime from '@/components/General/RelativeTime';
import { stripMentions } from '@/utils/mentionFormat';

const AVATAR_SIZE = 40;

/**
 * Reusable conversations list for sidebar / standalone display.
 * @param {string} selectedConversationID — conversation ID to highlight as active (optional)
 */
const ConversationsList = ({ selectedConversationID }) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { isNative } = usePlatform();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { conversationUnread } = useNotificationBadges();
    const canViewAllConversations = loggedInUser?.can('View All Conversations');
    const { data: conversations, isLoading: loadingConversations } = Query.conversations.byUserId({ userId: loggedInUser?.id });
    const { data: orgConversations } = Query.conversations.orgAll({ options: { enabled: canViewAllConversations } });
    const [searchText, setSearchText] = useState('');

    const togglePinMutation = useMutation({
        mutationFn: toggleConversationPin,
        onSuccess: (data) => {
            queryClient.setQueryData(['conversations', { userId: loggedInUser?.id }], old => {
                return old?.map(c => (c.id == data.conversationID) ? { ...c, pinnedForUser: data.isPinned } : c);
            });
        }
    });

    const leaveConversationMutation = useMutation({
        mutationFn: deleteConversation,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.conversations.byUserId(loggedInUser?.id) });
        }
    });

    const generateConversationName = (people) => {
        if(!people || people?.length == 0) return "Nobody";
        const peopleWithoutMe = people.filter(p => p.id != loggedInUser?.id);
        if(peopleWithoutMe?.length < 4) {
            if(peopleWithoutMe?.length == 1) return peopleWithoutMe[0]?.firstName + " " + peopleWithoutMe[0]?.lastName;
            return peopleWithoutMe.map((p, i) => i == 0 ? p.firstName : i == peopleWithoutMe?.length - 1 ? " & " + p.firstName : ", " + p.firstName);
        }
        return peopleWithoutMe[0].firstName + ", " + peopleWithoutMe[1].firstName + " & " + (peopleWithoutMe?.length - 2) + " others";
    };

    const handleConversationPress = useCallback((conversation) => {
        queryClient.setQueryData(QueryKeys.conversations.byId(conversation.id), conversation);
        router.push(`/connect/conversations/${conversation.id}`);
    }, [router, queryClient]);

    const renderSectionHeader = (title) => {
        if(title === "Pinned" && conversations?.filter(c => c.pinnedForUser).length == 0) return null;
        return (
            <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>{title}</Text>
            </View>
        );
    };

    const renderItem = ({ item }) => {
        if(typeof item === "string") {
            return renderSectionHeader(item);
        }
        const unreadCount = conversationUnread(item.id);
        const lastMessageSender = item?.lastMessage?.Sender || item?.People?.find(p => p.id == item?.lastMessage?.senderID);
        const avatarProfile = item?.avatarPath
            ? { id: item?.id, key: item?.avatarPath }
            : (item?.lastMessage && lastMessageSender?.id != loggedInUser?.id)
                ? lastMessageSender
                : item?.People?.find(p => p?.id != loggedInUser?.id) || item?.People?.[0];
        const isActive = selectedConversationID === item.id;
        const conversationName = item?.name === "*NONE*" ? generateConversationName(item?.People) : item?.name;
        const isOtherOrgConversation = !memberConversationIDs.has(item.id);

        return (
            <Pressable
                style={[styles.card, isActive && styles.cardActive]}
                onPress={() => handleConversationPress(item)}
            >
                <View style={styles.avatarWrap}>
                    <Avatar disableBorder size={AVATAR_SIZE} profile={avatarProfile} directory={item?.avatarPath ? "conversations/" : "avatars/"} />
                    <NotificationBadge count={unreadCount} size={18} style={[styles.badgeWrap, styles.badge]} testID={`conversation-badge-${item.id}`} />
                </View>
                <View style={styles.textContainer}>
                    <View style={styles.topRow}>
                        <Text style={styles.name} numberOfLines={1}>{conversationName}</Text>
                    </View>
                    <Text numberOfLines={2} style={styles.message}>
                        {!item?.lastMessage ? "No messages yet" : (
                            <>
                                <Text style={styles.senderPrefix}>{lastMessageSender?.id == loggedInUser?.id ? "Me" : lastMessageSender?.firstName}: </Text>
                                <Text>{stripMentions(item?.lastMessage?.text)}</Text>
                            </>
                        )}
                    </Text>
                </View>
                <View style={styles.menuWrap}>
                    {!isOtherOrgConversation && (
                        <DropdownMenu options={[
                            ...(!item?.membersByGroup ? [{ name: (item?.pinnedForUser) ? 'Unpin Conversation' : 'Pin Conversation', icon: (item?.pinnedForUser) ? dropdownIcons.unpin : dropdownIcons.pin, onSelect: () => {
                                setTimeout(() => togglePinMutation.mutate({ conversationID: item?.id, personID: loggedInUser?.id, isPinned: !item?.pinnedForUser }), 250);
                            }}] : []),
                            ...(!item?.membersByGroup ? [{ name: 'Leave Conversation', destructive: true, icon: { android: "logout", ios: "square.and.arrow.up", web: "logout" }, onSelect: () => {
                                setTimeout(() => {
                                    showAlert({ title: "Leave conversation?", cancelText: "Cancel", confirmText: "Leave", onConfirm: () => leaveConversationMutation.mutate({ conversationID: item?.id, personID: loggedInUser?.id }) });
                                }, 250);
                            }}] : []),
                        ]} />
                    )}
                    {item?.lastMessage?.sentAt && <RelativeTime time={item?.lastMessage?.sentAt} style={styles.time} />}
                </View>
            </Pressable>
        );
    };

    let contentLoaders = [];
    if(loadingConversations) {
        for(let i = 0; i < 8; i++) {
            contentLoaders.push(<View key={"loader" + i} style={styles.loaderRow}>
                <ContentLoader containerHeight={AVATAR_SIZE} containerWidth={AVATAR_SIZE} borderRadius={AVATAR_SIZE} staggerPosition={i} />
                <View style={styles.loaderDetail}>
                    <ContentLoader containerHeight={12} containerWidth="60%" staggerPosition={i} style={styles.loaderLine} />
                    <ContentLoader containerHeight={10} containerWidth="80%" staggerPosition={i + 1} style={styles.loaderLine} />
                </View>
            </View>);
        }
    }

    const memberConversationIDs = new Set((conversations || []).map(c => c.id));
    const otherOrgConversations = (orgConversations || []).filter(c => !memberConversationIDs.has(c.id));

    const conversationMatches = (c) => {
        if(!searchText) return true;
        const q = searchText.toLowerCase();
        const name = c.name === "*NONE*" ? "" : c.name?.toLowerCase() || "";
        const peopleText = (c.People || []).map(p => `${p.firstName || ""} ${p.lastName || ""}`.toLowerCase()).join(" ");
        return name.includes(q) || peopleText.includes(q);
    };

    const filteredPinned = useMemo(() => (conversations || []).filter(c => c.pinnedForUser && conversationMatches(c)), [conversations, searchText]);
    const filteredAll = useMemo(() => (conversations || []).filter(c => !c.pinnedForUser && conversationMatches(c)), [conversations, searchText]);
    const filteredOtherOrg = useMemo(() => otherOrgConversations.filter(conversationMatches), [otherOrgConversations, searchText]);

    const listData = loadingConversations ? [] : [
        ...(filteredPinned.length > 0 ? ["Pinned"] : []),
        ...filteredPinned,
        ...(filteredAll.length > 0 ? ["All My Conversations"] : []),
        ...filteredAll,
        ...(canViewAllConversations && filteredOtherOrg.length > 0 ? ["Other Conversations", ...filteredOtherOrg] : []),
    ];

    const listProps = {
        keyExtractor: (item) => item.id || item,
        keyboardShouldPersistTaps: "handled",
        showsVerticalScrollIndicator: false,
        data: listData,
        renderItem,
        ListFooterComponent: !loadingConversations
            ? <View style={styles.footer}>{filteredPinned.length == 0 && filteredAll.length == 0 && filteredOtherOrg.length == 0 ? <EmptyListPlaceholder icon="chatbox-ellipses-outline" spacing={10} message={searchText ? "No conversations match your search" : "No conversations yet!"} /> : <View />}</View>
            : <View style={styles.footerLoading}>{contentLoaders}</View>,
    };

    return (
        <View style={styles.container}>
            <View style={styles.searchContainer}>
                <SearchBox
                    searchText={searchText}
                    onChangeText={setSearchText}
                    placeholder="Search conversations"
                    textChangeDelay={150}
                    testID="ConversationsList"
                />
            </View>
            {isNative ? (
                <LegendList
                    recycleItems
                    estimatedItemSize={72}
                    getItemType={(item) => typeof item === "string" ? "sectionHeader" : "row"}
                    progressViewOffset={1000}
                    refreshing={false}
                    onRefresh={() => {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.conversations.byUserId(loggedInUser?.id) });
                        if(canViewAllConversations) queryClient.invalidateQueries({ queryKey: QueryKeys.conversations.orgAll() });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
                    }}
                    {...listProps}
                />
            ) : (
                <FlatList
                    refreshing={false}
                    onRefresh={() => {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.conversations.byUserId(loggedInUser?.id) });
                        if(canViewAllConversations) queryClient.invalidateQueries({ queryKey: QueryKeys.conversations.orgAll() });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
                    }}
                    {...listProps}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: {
        flex: 1,
    },
    searchContainer: {
        paddingHorizontal: 8,
        paddingTop: 10,
        paddingBottom: 4,
    },
    sectionHeader: {
        paddingHorizontal: 14,
        paddingTop: 16,
        paddingBottom: 6,
    },
    sectionHeaderText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    card: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
        paddingLeft: 10,
        paddingRight: 4,
        marginHorizontal: 6,
        marginVertical: 5,
        borderRadius: 10,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        _web: {
            transition: "all 0.15s ease",
            _hover: { backgroundColor: colors.hoverCard, transform: [{ scale: 1.01 }], boxShadow: shadows.cardHover },
        },
    },
    cardActive: {
        backgroundColor: colors.primaryContainer,
    },
    avatarWrap: {
        marginRight: 12,
        position: 'relative',
    },
    badgeWrap: {
        position: 'absolute',
        top: -4,
        right: -4,
        zIndex: 1,
    },
    badge: {
        fontSize: 10,
    },
    textContainer: {
        flex: 1,
        justifyContent: 'center',
    },
    topRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    name: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
        flexShrink: 1,
    },
    time: {
        fontSize: 10,
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        marginTop: 2,
        flexShrink: 0,
    },
    message: {
        fontSize: 12,
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        marginTop: 3,
    },
    senderPrefix: {
        color: colors.text,
        fontFamily: fonts.bold,
    },
    menuWrap: {
        marginLeft: 4,
        alignItems: 'center',
        alignSelf: 'center',
    },
    loaderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginHorizontal: 10,
        marginVertical: 6,
    },
    loaderDetail: {
        marginHorizontal: 10,
        flex: 1,
    },
    loaderLine: {
        marginVertical: 3,
    },
    footer: {
        paddingBottom: 80,
    },
    footerLoading: {
        marginHorizontal: 5,
    },
}));

export default ConversationsList;
